/**
 * InventoryItemPicker
 *
 * A shared "Pick from Inventory" autocomplete that works inside any
 * ArrayInput / SimpleFormIterator. When an inventory item is selected it
 * auto-fills sibling fields:
 *   • description  → inventory_items.name
 *   • hsn_code     → inventory_items.hsn_code
 *   • unit         → inventory_items.unit
 *   • tax_rate     → hsn_codes.gst_rate  (looked up by code)
 *   • priceField   → inventory_items.unit_price  ('unit_price' or 'estimated_unit_cost')
 *
 * Usage (inside SimpleFormIterator):
 *   <InventoryItemPickerWrapper priceField="unit_price" setTaxRate />
 *   <InventoryItemPickerWrapper priceField="estimated_unit_cost" />
 */

import { Package, X } from 'lucide-react';
import { useGetList, useSimpleFormIterator, useSimpleFormIteratorItem } from 'ra-core';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';


// ── Core picker ───────────────────────────────────────────────────────────────
interface InventoryItemPickerProps {
    /** The form source path for this item, e.g. "items.0" */
    itemSource: string;
    /** Which price field to populate. Pass null to skip price auto-fill (e.g. DC items). Default: 'unit_price' */
    priceField?: 'unit_price' | 'estimated_unit_cost' | null;
    /** Whether to auto-set tax_rate from the HSN code's gst_rate. Default: true */
    setTaxRate?: boolean;
}

const InventoryItemPicker = ({ itemSource, priceField = 'unit_price', setTaxRate = true }: InventoryItemPickerProps) => {
    const { setValue } = useFormContext();
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);

    // Derive pill visibility from form state — not local state — so clear+reselect
    // and component remounts stay in sync (D-4 fix).
    const currentDesc = useWatch({ name: `${itemSource}.description` });
    const currentHsn  = useWatch({ name: `${itemSource}.hsn_code` });
    const showPill = !!currentDesc;

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Search inventory items
    const { data: inventoryItems } = useGetList('inventory_items', {
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'name', order: 'ASC' },
        filter: query.trim().length >= 2
            ? { 'name@ilike': query.trim() }
            : {},
    }, { enabled: query.trim().length >= 2 });

    // Fetch GST rate from hsn_codes when we have a code
    const [pendingHsnCode, setPendingHsnCode] = useState<string | null>(null);
    const { data: hsnData } = useGetList('hsn_codes', {
        pagination: { page: 1, perPage: 1 },
        filter: pendingHsnCode ? { code: pendingHsnCode } : {},
    }, { enabled: !!pendingHsnCode && setTaxRate });

    useEffect(() => {
        if (hsnData && hsnData.length > 0 && pendingHsnCode) {
            const gstRate = hsnData[0].gst_rate ?? 0;
            setValue(`${itemSource}.tax_rate`, gstRate, { shouldDirty: true });
            setPendingHsnCode(null);
        }
    }, [hsnData, pendingHsnCode, itemSource, setValue]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (item: any) => {
        setQuery('');
        setOpen(false);

        // Auto-fill sibling fields
        setValue(`${itemSource}.description`, item.name,            { shouldDirty: true });
        setValue(`${itemSource}.hsn_code`,    item.hsn_code || '',  { shouldDirty: true });
        setValue(`${itemSource}.unit`,        item.unit || 'Nos',   { shouldDirty: true });
        if (priceField) {
            setValue(`${itemSource}.${priceField}`, item.unit_price || 0, { shouldDirty: true });
        }

        // Trigger GST rate lookup
        if (setTaxRate && item.hsn_code) {
            setPendingHsnCode(item.hsn_code);
        }
    };

    const handleClear = () => {
        setQuery('');
        setValue(`${itemSource}.description`, '', { shouldDirty: true });
        setValue(`${itemSource}.hsn_code`,    '', { shouldDirty: true });
        if (priceField) {
            setValue(`${itemSource}.${priceField}`, 0, { shouldDirty: true });
        }
    };

    return (
        <div ref={containerRef} className="relative w-full">
            {showPill ? (
                // ── Selected pill ────────────────────────────────────────────
                <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-md text-xs group">
                    <Package className="w-3 h-3 text-indigo-500 shrink-0" />
                    <span className="font-semibold text-indigo-700 truncate flex-1">{currentDesc}</span>
                    {currentHsn && (
                        <span className="font-mono text-[10px] text-indigo-400 shrink-0">{currentHsn}</span>
                    )}
                    <button
                        type="button"
                        onClick={handleClear}
                        className="ml-auto text-indigo-300 hover:text-red-500 transition-colors shrink-0"
                        title="Clear inventory selection"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>
            ) : (
                // ── Search input ──────────────────────────────────────────────
                <div className="relative">
                    <Package className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        placeholder="Pick from Inventory…"
                        className="w-full pl-6 pr-2 py-1.5 text-xs border border-dashed border-indigo-200 rounded-md bg-indigo-50/40 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:bg-white transition"
                        onChange={e => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                    />
                </div>
            )}

            {/* ── Dropdown suggestions ─────────────────────────────────────── */}
            {open && !showPill && (inventoryItems?.length ?? 0) > 0 && (
                <div className="absolute z-50 top-full mt-1 left-0 w-full min-w-[260px] bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b">
                        Inventory Items — click to auto-fill
                    </div>
                    {inventoryItems?.map((item: any) => (
                        <div
                            key={item.id}
                            className="flex items-start gap-2 px-3 py-2 hover:bg-indigo-50 cursor-pointer border-b border-slate-50 last:border-b-0 transition-colors"
                            onMouseDown={e => { e.preventDefault(); handleSelect(item); }}
                        >
                            <Package className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-xs text-slate-800 truncate">{item.name}</span>
                                    <span className="font-mono text-[10px] text-slate-400 shrink-0">{item.sku}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {item.hsn_code && (
                                        <span className="text-[10px] text-slate-500">HSN: {item.hsn_code}</span>
                                    )}
                                    {item.unit && (
                                        <span className="text-[10px] text-slate-400">{item.unit}</span>
                                    )}
                                    {item.unit_price != null && (
                                        <span className="text-[10px] text-emerald-600 font-medium ml-auto">
                                            ₹{Number(item.unit_price).toLocaleString('en-IN')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {open && !showPill && query.trim().length >= 2 && (inventoryItems?.length ?? 0) === 0 && (
                <div className="absolute z-50 top-full mt-1 left-0 w-full bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs text-slate-400 italic">
                    No inventory items found for "{query}"
                </div>
            )}
        </div>
    );
};

// ── Wrapper that reads context from SimpleFormIterator ────────────────────────
interface WrapperProps {
    priceField?: 'unit_price' | 'estimated_unit_cost' | null;
    setTaxRate?: boolean;
}

const InventoryItemPickerWrapperInside = ({ priceField, setTaxRate }: WrapperProps) => {
    const item     = useSimpleFormIteratorItem();
    const iterator = useSimpleFormIterator();
    if (!item || !iterator) return null;
    return (
        <InventoryItemPicker
            itemSource={`${iterator.source}.${item.index}`}
            priceField={priceField}
            setTaxRate={setTaxRate}
        />
    );
};

export const InventoryItemPickerWrapper = ({ priceField = 'unit_price', setTaxRate = true }: WrapperProps) => {
    try {
        return <InventoryItemPickerWrapperInside priceField={priceField} setTaxRate={setTaxRate} />;
    } catch {
        return null;
    }
};
